/* eslint-disable import/prefer-default-export */
import {
  OrganizationInfo,
  AccountInfo,
  AWSInfo,
  GCPAccountInfo,
  AzureInfo,
  OktaInfo,
  CloudTypeEnum,
  OrganizationInfo_IncludeAccountIdListEntry
} from '@src/endpoints/services/base.service'
import type { Model } from '@src/redux/reducers/organization/models'
import { isArray, isEmpty } from 'lodash'
import type { ValidateOrganizationReq } from '@src/endpoints/services/policyReader.service'

const clean = <T = {}>(obj: T) =>
  Object.keys(obj)
    .filter(k => typeof obj[k] === 'boolean' || !isEmpty(obj[k]))
    .reduce(
      (newObj, k) =>
        typeof obj[k] === 'object' && !isArray(obj[k])
          ? { ...newObj, [k]: clean(obj[k]) }
          : { ...newObj, [k]: obj[k] },
      {}
    )

export class TransferModel {
  public name = ''
  public awsInfo: AWSInfo
  public gcpInfo: GCPAccountInfo
  public azureInfo: AzureInfo
  public oktaInfo: OktaInfo
  public includeAccountIdList?: OrganizationInfo_IncludeAccountIdListEntry
  private readonly _original: OrganizationInfo | AccountInfo = {}
  constructor(options: OrganizationInfo)
  constructor(options: AccountInfo)
  constructor(options: OrganizationInfo & AccountInfo) {
    this._original = options
    this.name = options?.organization_name || options?.account_name || ''
    this.awsInfo = options?.aws_organization_info || options?.aws_info || {}
    this.azureInfo =
      options?.azure_organization_info || options?.azureInfo || {}
    this.gcpInfo = options?.gcp_organization_info || options?.gcp_info || {}
    this.oktaInfo = options?.okta_info || {}
    this.includeAccountIdList = options?.include_account_id_list
  }

  get original() {
    return this._original
  }

  get isPrePopulated() {
    const isIDPAzure = this._original?.cloud_type === CloudTypeEnum.IDP_AZURE_AD
    return isIDPAzure && isEmpty(this.azureInfo?.app_instance_id)
  }

  public convertToModel = (): Model => ({
    original: this.original,
    name: this.name,
    cloud_type: this.original.cloud_type,
    awsInfo: this.awsInfo,
    azureInfo: this.azureInfo,
    gcpInfo: this.gcpInfo,
    oktaInfo: this.oktaInfo,
    includeAccountIdList: this.includeAccountIdList,
    correlatedSource: null
  })
  static convertToValidateOrganizationReq = (
    data: Model
  ): ValidateOrganizationReq => {
    const { organization_uid, uid } = (data?.original ||
      {}) as OrganizationInfo & AccountInfo
    const isOrganization = !isEmpty(organization_uid)
    return clean({
      ...data?.original,
      uid: !isOrganization ? uid : undefined,
      cloud_type: data?.cloud_type,
      organization_name: data?.name,
      aws_organization_info: data?.awsInfo,
      azure_organization_info: data?.azureInfo,
      gcp_organization_info: data?.gcpInfo,
      okta_idp_info: data?.oktaInfo
    })
  }
  static convertToAccountInfo = (data: Model): AccountInfo =>
    clean({
      ...(data?.original as AccountInfo),
      cloud_type: data?.cloud_type,
      azureInfo: data?.azureInfo,
      account_name: data?.name,
      gcp_info: data?.gcpInfo,
      aws_info: data?.awsInfo,
      okta_info: data?.oktaInfo
    })
  static convertToOrganizationInfo = (data: Model): OrganizationInfo =>
    clean({
      ...(data?.original as OrganizationInfo),
      cloud_type: data?.cloud_type,
      organization_name: data?.name,
      aws_organization_info: data?.awsInfo,
      azure_organization_info: data?.azureInfo,
      gcp_organization_info: data?.gcpInfo,
      include_account_id_list: data?.includeAccountIdList
    })
}
